import DOMPurify from 'dompurify';
import Hls from 'hls.js';
import $ from 'jquery';
import _isFunction from 'lodash/isFunction';

import * as MimeType from 'constants/mimes';
import { CLOUDFRONT_URI, LOOM_URI } from 'constants/routes';
import { LOOM_VERSION } from 'constants/runtimeConfig';
import { setDocumentTitle } from 'utilities/notifications';
import { isFromPublicSharePage } from 'utilities/url';

import * as analytics from '../utilities/analytics';
import {
  DEFAULT_PASSWORD_PROTECTED_THUMBNAIL_PATH,
  DEFAULT_PRIVATE_THUMBNAIL_PATH,
  THUMB_GIF_PLAY,
} from '../../../shared/constants/video';
import {
  hasPassword,
  isPrivate,
  isPublicVideo,
} from '../../../shared/utilities/video';

const CANT_PLAY_TYPE_REGEX = /no/;
const POLLING_VIDEO_RAW = 1000;
const POLLING_VIDEO_TRANSCODED = 8000;

const POLLING_VIDEO_RAW_MAX_RETRIES = 10;

export const postSessionRawURL = (
  sessionID,
  password,
  cb,
  forceOriginal = false,
  supportedMimeTypes = undefined,
  clipId = undefined
) => {
  function _requestRawURL(counter) {
    const { anonID, deviceID } = analytics.getAnalyticsIds();

    const dataToSend = {
      force_original: forceOriginal,
      password,
      anonID,
      deviceID,
      client_name: 'web',
      client_version: LOOM_VERSION,
    };

    if (supportedMimeTypes) {
      dataToSend.supported_mime_types = supportedMimeTypes;
    }

    if (clipId) {
      dataToSend.clip_id = clipId;
    }

    $.ajax({
      method: 'POST',
      url: `/api/campaigns/sessions/${sessionID}/raw-url`,
      data: JSON.stringify(dataToSend),
      dataType: 'json',
    }).done(response => {
      if (response && response.url != null) {
        return cb(response.url, response.part_credentials || {});
      }

      if (counter >= POLLING_VIDEO_RAW_MAX_RETRIES) {
        return cb(null, {});
      }

      window.setTimeout(() => {
        _requestRawURL(counter + 1);
      }, POLLING_VIDEO_RAW);
    });
  }

  const initialCounter = 0;

  _requestRawURL(initialCounter);
};

let timeoutId = null;
let shouldCancel = false;

export function cancelPostSessionTranscodedURL() {
  shouldCancel = true;

  if (!timeoutId) {
    return;
  }

  window.clearTimeout(timeoutId);
  timeoutId = null;
}

export const postSessionTranscodedURL = (
  sessionID,
  password,
  cb,
  forceOriginal = false
) => {
  shouldCancel = false;

  function _requestTranscodedURL() {
    const { anonID, deviceID } = analytics.getAnalyticsIds();

    $.ajax({
      method: 'POST',
      url: '/api/campaigns/sessions/' + sessionID + '/transcoded-url',
      data: JSON.stringify({
        force_original: forceOriginal,
        password,
        anonID,
        deviceID,
      }),
      dataType: 'json',
    }).done(response => {
      if (shouldCancel) {
        return;
      }

      if (response && response.url != null) {
        return cb(response.url);
      }

      timeoutId = window.setTimeout(
        _requestTranscodedURL,
        POLLING_VIDEO_TRANSCODED
      );
    });
  }

  _requestTranscodedURL();

  return cancelPostSessionTranscodedURL;
};

export const canPlayHlsNative = () => {
  // https://stackoverflow.com/a/3587475/696130
  return Boolean(
    document
      .createElement('video')
      .canPlayType(MimeType.M3U8)
      .replace(CANT_PLAY_TYPE_REGEX, '')
  );
};

export const hlsSupported = () => Hls.isSupported();

// http://stackoverflow.com/a/31133401/696130
export const isPlaying = video => {
  return Boolean(
    !(
      video.currentTime > 0 &&
      !video.paused &&
      !video.ended &&
      video.readyState > 2
    )
  );
};

export const getSupportedMimeTypes = () => {
  const videoElement = document.createElement('video');

  // MP4 is the basic format any player should be able to play
  const supportedMimeTypes = [MimeType.MP4];

  if (!_isFunction(videoElement.canPlayType)) {
    return supportedMimeTypes;
  }

  if (videoElement.canPlayType('video/webm; codecs="vp8, opus"') !== '') {
    supportedMimeTypes.push(MimeType.DASH);
  }

  if (videoElement.canPlayType(MimeType.M3U8) !== '' || Hls.isSupported()) {
    supportedMimeTypes.push(MimeType.M3U8);
  }

  // MP4 is included in the initialization

  return supportedMimeTypes;
};

export const getPosterUrl = video => {
  const {
    needs_password: needsPassword,
    viewer_needs_permission: needsPermission,
    current_user_is_owner: isOwner,
  } = video;

  if (!video?.defaultThumbnails?.default) {
    return null;
  }

  if ((needsPassword || needsPermission) && !isOwner) {
    return null;
  }

  return `${CLOUDFRONT_URI}/${video.defaultThumbnails.default}`;
};

export const getDuration = video => {
  const { video_properties: videoProperties } = video;
  const { trim_id: trimId } = video.processing_information;

  if (trimId != null) {
    return videoProperties.trim_duration;
  }

  return videoProperties.duration;
};

export const getEmbedCode = (videoId, embedWidth, embedHeight) => {
  const embedURL = `${LOOM_URI}/embed/${videoId}`;

  return (
    `<iframe width="${embedWidth}" ` +
    `height="${embedHeight}" src="${embedURL}" ` +
    'frameborder="0" webkitallowfullscreen mozallowfullscreen ' +
    'allowfullscreen></iframe>'
  );
};

// iframes need a wrapping div and specific css in order allow fluid responsiveness
// https://css-tricks.com/NetMag/FluidWidthVideo/Article-FluidWidthVideo.php
export const getResponsiveEmbedCode = (videoId, heightAspectRatio) => {
  const embedURL = `${LOOM_URI}/embed/${videoId}`;

  const padding = `${heightAspectRatio * 100}%`;
  const wrapperStyles = `position: relative; padding-bottom: ${padding}; height: 0;`;
  const iframeStyles =
    'position: absolute; top: 0; left: 0; width: 100%; height: 100%;';

  return (
    `<div style="${wrapperStyles}"><iframe src="${embedURL}" ` +
    'frameborder="0" webkitallowfullscreen mozallowfullscreen ' +
    `allowfullscreen style="${iframeStyles}"></iframe></div>`
  );
};
export const isAliasVideoUrl = url => /\/a\/[a-z0-9]{32}$/.test(url);
export const isShareVideoUrl = url => /\/share\/[a-z0-9]{32}$/.test(url);
export const getShareVideoUrl = video => `${LOOM_URI}/share/${video.id}`;
export const getAliasVideoUrl = id => `${LOOM_URI}/a/${id}`;
export const getAnimatedPreview = video => {
  if (
    isPublicVideo(video) &&
    video.thumbnails[THUMB_GIF_PLAY] &&
    video.use_gif
  ) {
    return `${CLOUDFRONT_URI}/${video.thumbnails[THUMB_GIF_PLAY]}`;
  } else if (isPrivate(video)) {
    return `${CLOUDFRONT_URI}/${DEFAULT_PRIVATE_THUMBNAIL_PATH['.gif']}`;
  } else if (hasPassword(video) || video.is_protected) {
    // The second part of this clause is because the video data dpes not always
    // have the password or have has_password set even when it has a password.
    return `${CLOUDFRONT_URI}/${DEFAULT_PASSWORD_PROTECTED_THUMBNAIL_PATH['.gif']}`;
  }

  return null;
};

export const getAnimatedPreviewEmbed = video => {
  let name = video.name;

  if (isPrivate(video)) {
    name = 'Private Video';
  } else if (hasPassword(video) || video.is_protected) {
    name = 'Password Protected Video';
  }

  return `<a href="${getShareVideoUrl(video)}">
    <p>${DOMPurify.sanitize(name)} - Watch Video</p>
    <img style="max-width:300px;" src="${getAnimatedPreview(video)}">
  </a>`;
};

export function setDocumentTitleForVideo(state) {
  if (isFromPublicSharePage().fromPublicSharePage) {
    const {
      share_video: { video_name },
    } = state;

    return setDocumentTitle(video_name);
  }

  const {
    share_video: { video_name },
  } = state;

  document.title = video_name;
}

export const prioritizeSignedThumbnails = video => {
  if (video.thumbnails && video.signedThumbnails) {
    for (const [key, value] of Object.entries(video.signedThumbnails)) {
      video.thumbnails[key] = value;
    }
  }

  if (video.defaultThumbnails && video.signedDefaultThumbnails) {
    for (const [key, value] of Object.entries(video.signedDefaultThumbnails)) {
      video.defaultThumbnails[key] = value;
    }
  }

  return video;
};
